import { memo } from 'react'
import { useTheme } from '@app/components/themes/theme'
import { Button } from '../general'
import { classNames } from '@app/utils/classes'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { ProfileInputCell } from '../general/cells/profile-input'
import { User } from '@app/types'
import { twMerge } from 'tailwind-merge'

// theme active style btn
const getThemeBtnStyle = (active?: boolean) =>
  classNames(
    active
      ? 'outline-blue-600 text-blue-600 dark:text-blue-500 dark:outline-blue-500'
      : 'text-gray-800 dark:text-gray-100 outline-gray-300 dark:outline-gray-700',
    'min-w-[5.5rem] text-sm',
    'outline outline-offset-2 p-0.5 hover:text-blue-600 hover:outline-blue-600 dark:hover:outline-blue-500 dark:hover:text-blue-500'
  )

const themeStyle = 'h-22 flex place-items-center rounded'
const tipStyle = 'py-1 inline px-3 md:px-6 text-sm'
const btnStyle =
  'p-0 md:p-0 lg:p-0 flex-1 w-full hover:opacity-95 hover:outline'
const pillStyle = ''
const tipContainerStyle = 'pt-3 pb-1.5 text-center'
const tipHighlightStyle =
  'text-blue-800 dark:text-blue-500 dark:border-blue-500 font-medium'

const ThemesViewComponent = ({
  user,
  loading,
}: {
  user?: User
  loading?: boolean
}) => {
  const { t } = useTranslation('dashboard-theme')

  const { theme, setTheme } = useTheme()

  const onLightThemeEvent = () => setTheme('light')
  const onDarkThemeEvent = () => setTheme('dark')
  const onSystemThemeEvent = () => setTheme('system')

  const lightThemeActive = theme === 'light'

  return (
    <ProfileInputCell
      title={t('settings:appearance')}
      subTitle={t('settings:theme-details')}
      skeletonLoad={!user && loading}
      submitLabel={{
        title: `${t('settings:cu-theme')} ${
          theme ? t(theme.toLowerCase()).toLowerCase() : theme
        }`,
      }}
    >
      <div className='flex gap-x-4 py-2'>
        <div className='flex-1 place-items-center'>
          <Button
            onClick={onLightThemeEvent}
            className={twMerge(getThemeBtnStyle(lightThemeActive), btnStyle)}
            border={false}
            nopadding
          >
            <div>
              <div className='relative overflow-hidden rounded'>
                <div
                  className={classNames(themeStyle, 'flex-col px-2 gap-1 py-2')}
                >
                  <div className='flex bg-gray-800 h-8 w-full rounded border dark:border-gray-600 z-10'></div>
                  <div className='flex h-8 w-full rounded border dark:border-gray-600 z-10'></div>
                  <div className='flex h-8 w-full rounded border dark:border-gray-600 z-10'></div>
                </div>

                <div className={'absolute z-1 left-0 right-0 bottom-0 top-0'}>
                  <div className='flex w-full h-full'>
                    <div className='flex flex-1 bg-gray-800 h-full'></div>
                    <div className='flex flex-1 bg-white h-full'></div>
                  </div>
                </div>
              </div>
              <span className='sr-only'> {t('light')}</span>
            </div>
          </Button>
          <div className={tipContainerStyle}>
            <div
              className={classNames(
                tipStyle,
                lightThemeActive ? tipHighlightStyle : pillStyle
              )}
            >
              {t('light')}
            </div>
          </div>
        </div>
        <div className='flex-1 place-items-center'>
          <Button
            onClick={onDarkThemeEvent}
            className={classNames(getThemeBtnStyle(theme === 'dark'), btnStyle)}
            border={false}
            nopadding
          >
            <div>
              <div
                className={classNames(
                  themeStyle,
                  'px-2 flex-col gap-1 py-2 bg-gray-800'
                )}
              >
                <div className='flex bg-gray-800 h-8 w-full rounded border dark:border-gray-600'></div>
                <div className='flex bg-gray-800 h-8 w-full rounded border dark:border-gray-600'></div>
                <div className='flex bg-gray-800 h-8 w-full rounded border dark:border-gray-600'></div>
              </div>
              <span className='sr-only'> {t('dark')}</span>
            </div>
          </Button>
          <div className={tipContainerStyle}>
            <div
              className={classNames(
                tipStyle,
                theme === 'dark' ? tipHighlightStyle : pillStyle
              )}
            >
              {t('dark')}
            </div>
          </div>
        </div>
        <div className='flex-1 place-items-center'>
          <Button
            onClick={onSystemThemeEvent}
            className={classNames(
              getThemeBtnStyle(theme === 'system'),
              btnStyle
            )}
            border={false}
            nopadding
          >
            <div>
              <div
                className={classNames(
                  themeStyle,
                  'px-2 flex-col gap-1 py-2 bg-gray-200'
                )}
              >
                <div className='flex bg-gray-800 h-8 w-full rounded border dark:border-gray-600'></div>
                <div className='flex bg-gray-400 h-8 w-full rounded border dark:border-gray-600'></div>
                <div className='flex bg-gray-400 h-8 w-full rounded border dark:border-gray-600'></div>
              </div>
              <span className='sr-only'> {t('system')}</span>
            </div>
          </Button>
          <div className={tipContainerStyle}>
            <div
              className={classNames(
                tipStyle,
                theme === 'system' ? tipHighlightStyle : pillStyle
              )}
            >
              {t('system')}
            </div>
          </div>
        </div>
      </div>
    </ProfileInputCell>
  )
}

export const ThemesView = memo(ThemesViewComponent)
